import type { MutableRefObject } from "react";
import {
  selectTouchDistance,
  selectTouchMoveZoomParams,
  selectTouchPair,
  selectWheelScaleFactor,
  type ApplyZoomAtAnchorParams,
  type PinchState,
} from "./gantt-zoom-control-math";

interface CreateGanttZoomPointerListenersParams {
  applyZoomAtAnchor: (params: ApplyZoomAtAnchorParams) => void;
  fitPxPerDayRef: MutableRefObject<number>;
  pinchStateRef: MutableRefObject<PinchState | null>;
  pxPerDayOverrideRef: MutableRefObject<number | null>;
  timelineNode: HTMLDivElement;
}

interface GanttZoomPointerHandlers {
  handleWheel: (event: WheelEvent) => void;
  handleTouchStart: (event: TouchEvent) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: () => void;
}

function createWheelZoomHandler({
  applyZoomAtAnchor,
  timelineNode,
}: {
  applyZoomAtAnchor: (params: ApplyZoomAtAnchorParams) => void;
  timelineNode: HTMLDivElement;
}): (event: WheelEvent) => void {
  return (event: WheelEvent): void => {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }

    event.preventDefault();

    const rect = timelineNode.getBoundingClientRect();
    applyZoomAtAnchor({
      anchorViewportX: event.clientX - rect.left,
      scaleFactor: selectWheelScaleFactor(event),
    });
  };
}

function createTouchStartZoomHandler({
  fitPxPerDayRef,
  pinchStateRef,
  pxPerDayOverrideRef,
}: {
  fitPxPerDayRef: MutableRefObject<number>;
  pinchStateRef: MutableRefObject<PinchState | null>;
  pxPerDayOverrideRef: MutableRefObject<number | null>;
}): (event: TouchEvent) => void {
  return (event: TouchEvent): void => {
    const touchPair = selectTouchPair(event.touches);
    if (!touchPair) {
      pinchStateRef.current = null;
      return;
    }

    pinchStateRef.current = {
      initialDistance: selectTouchDistance(touchPair),
      initialPxPerDay: pxPerDayOverrideRef.current ?? fitPxPerDayRef.current,
    };
  };
}

function createTouchMoveZoomHandler({
  applyZoomAtAnchor,
  fitPxPerDayRef,
  pinchStateRef,
  pxPerDayOverrideRef,
  timelineNode,
}: CreateGanttZoomPointerListenersParams): (event: TouchEvent) => void {
  return (event: TouchEvent): void => {
    const zoomParams = selectTouchMoveZoomParams({
      touches: event.touches,
      pinchState: pinchStateRef.current,
      timelineNode,
      currentPxPerDay: pxPerDayOverrideRef.current ?? fitPxPerDayRef.current,
    });
    if (!zoomParams) {
      return;
    }

    event.preventDefault();
    applyZoomAtAnchor(zoomParams);
  };
}

function createTouchEndZoomHandler({
  pinchStateRef,
}: {
  pinchStateRef: MutableRefObject<PinchState | null>;
}): () => void {
  return (): void => {
    pinchStateRef.current = null;
  };
}

function createGanttZoomPointerHandlers(
  params: CreateGanttZoomPointerListenersParams,
): GanttZoomPointerHandlers {
  return {
    handleWheel: createWheelZoomHandler(params),
    handleTouchStart: createTouchStartZoomHandler(params),
    handleTouchMove: createTouchMoveZoomHandler(params),
    handleTouchEnd: createTouchEndZoomHandler(params),
  };
}

export function createGanttZoomPointerListeners(
  params: CreateGanttZoomPointerListenersParams,
): () => void {
  const { timelineNode } = params;
  const handlers = createGanttZoomPointerHandlers(params);

  timelineNode.addEventListener("wheel", handlers.handleWheel, {
    passive: false,
  });
  timelineNode.addEventListener("touchstart", handlers.handleTouchStart, {
    passive: true,
  });
  timelineNode.addEventListener("touchmove", handlers.handleTouchMove, {
    passive: false,
  });
  timelineNode.addEventListener("touchend", handlers.handleTouchEnd, {
    passive: true,
  });
  timelineNode.addEventListener("touchcancel", handlers.handleTouchEnd, {
    passive: true,
  });

  return () => {
    timelineNode.removeEventListener("wheel", handlers.handleWheel);
    timelineNode.removeEventListener("touchstart", handlers.handleTouchStart);
    timelineNode.removeEventListener("touchmove", handlers.handleTouchMove);
    timelineNode.removeEventListener("touchend", handlers.handleTouchEnd);
    timelineNode.removeEventListener("touchcancel", handlers.handleTouchEnd);
  };
}
